import { useEffect, useState, useCallback } from "react";
import { useDtfArtworks } from "@design-lab/entities/artwork/api/useDtfArtworks";
import { STANDARD_DTF_CATALOG_NUMBER } from "@design-lab/shared/constants/constants";
import { useDtfChannel } from "@design-lab/shared/hooks/useDtfChannel";
import type { Artwork } from "@design-lab/shared/types/artwork";

interface UseLibraryArtworkLoaderProps {
  onArtworksLoaded: (artworks: Artwork[]) => void;
}

export function useLibraryArtworkLoader({ onArtworksLoaded }: UseLibraryArtworkLoaderProps) {
  const [dtfChannelUuid, setDtfChannelUuid] = useState<string>("");
  const [pendingArtworkIds, setPendingArtworkIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const channel = useDtfChannel();

  // Get DTF channel UUID
  useEffect(() => {
    const fetchChannelUuid = async () => {
      const uuid = await channel.retrieveDtfChannelUuid();
      setDtfChannelUuid(uuid || "");
    };

    fetchChannelUuid();
  }, [channel]);

  // Query for artwork details when artwork IDs are provided
  const queryParams = {
    apiType: "details" as const,
    catalogNumber: STANDARD_DTF_CATALOG_NUMBER,
    artworkIds: pendingArtworkIds.map(id => id.toString()),
    dtfChannelUuid,
    perPage: pendingArtworkIds.length
  };

  const { data: artworkDetails, isLoading: isQueryLoading } = useDtfArtworks(
    pendingArtworkIds.length > 0 && dtfChannelUuid ? queryParams : {}
  );

  useEffect(() => {
    if (!artworkDetails || pendingArtworkIds.length === 0) return;

    // Process the artwork data from the response
    const { artworks } = artworkDetails as any;
    if (!artworks || artworks.length === 0) return;

    // Convert the artwork data to the format expected by transfers
    const processedArtworks = artworks.map((artwork: any) => {
      // Match the format used by design lab
      const processed = {
        ...artwork.ordered,
        id: artwork.id.toString(),
        originalImageUrl: artwork.original?.imageUrl || artwork.ordered?.imageUrl || "",
        url: artwork.ordered?.noBgUrl || artwork.ordered?.imageUrl || "",
        noBgUrl: artwork.ordered?.noBgUrl || artwork.ordered?.imageUrl || "",
        noBgNoTrimUrl: artwork.ordered?.noBgNoTrimUrl || artwork.ordered?.imageUrl || "",
        withBgUrl:
          artwork.ordered?.withBgUrl ||
          artwork.original?.imageUrl ||
          artwork.ordered?.imageUrl ||
          "",
        canRemoveBg: artwork.ordered?.canRemoveBg ?? true,
        removeBackground: artwork.ordered?.removeBackground ?? false,
        enhanceImage: artwork.ordered?.enhanceImage ?? true,
        enhancementTier: artwork.ordered?.enhancementTier || "full_enhancements"
      };

      return processed;
    });

    onArtworksLoaded(processedArtworks);
    // Clear pending IDs after processing
    setPendingArtworkIds([]);
  }, [artworkDetails, pendingArtworkIds, onArtworksLoaded]);

  const loadArtworks = useCallback((ids: number[]) => {
    if (ids.length === 0) return;
    setIsLoading(true);
    setPendingArtworkIds(ids);
  }, []);

  useEffect(() => {
    if (!isQueryLoading) {
      setIsLoading(false);
    }
  }, [isQueryLoading]);

  return {
    loadArtworks,
    isLoading: isLoading || isQueryLoading
  };
}
